import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine, LabelList } from 'recharts';
interface BarChartVisualProps {
  data: Array<{
    label: string;
    value: number;
  }>;
  xAxisLabel?: string;
  yAxisLabel?: string;
  barColor?: string;
  hideValues?: boolean;
  valueDisplay?: 'tooltip' | 'on-bar' | 'on-axis-line';
  mode?: 'stimulus' | 'support';
}
export function BarChartVisual({
  data,
  xAxisLabel = '',
  yAxisLabel = '',
  barColor = '#6366f1',
  hideValues = true,
  valueDisplay,
  mode = 'stimulus'
}: BarChartVisualProps) {
  // In support mode, we might want to force showing values to help explain the answer
  const effectiveHideValues = mode === 'support' ? false : hideValues;
  const effectiveValueDisplay = valueDisplay || (effectiveHideValues ? 'on-axis-line' : 'on-bar');
  const CustomTooltip = ({
    active,
    payload
  }: {
    active?: boolean;
    payload?: Array<{
      payload: {
        label: string;
        value: number;
      };
    }>;
  }) => {
    if (!active || !payload?.[0]) return null;
    const item = payload[0].payload;
    return <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-slate-200 text-sm" data-id="element-13048" data-mp="2z6e6ep3gxsr">
        <span className="font-semibold text-slate-700" data-id="element-13049" data-mp="3k79uri0lwydc">{item.label}</span>
        {!effectiveHideValues && <span className="text-slate-500 ml-2" data-id="element-13050" data-mp="3p5bpj07eam4n">{item.value}</span>}
      </div>;
  };
  return <div className="w-full max-w-3xl mx-auto flex flex-col items-center" data-id="element-13051" data-mp="gq1jjh0dwwrl">
      {/* Visually hidden table for screen readers */}
      <table className="sr-only" data-id="element-13052" data-mp="lq5yh2ln9jfp">
        <caption data-id="element-13053" data-mp="6zgi7ltbte4z">
          Staafdiagram data
          {xAxisLabel || yAxisLabel ? ` voor ${xAxisLabel} en ${yAxisLabel}` : ''}
        </caption>
        <thead data-id="element-13054" data-mp="bwp68315oce5">
          <tr data-id="element-13055" data-mp="bw708eooyjt1">
            <th scope="col" data-id="element-13056" data-mp="bvxbeufduiox">{xAxisLabel || 'Categorie'}</th>
            <th scope="col" data-id="element-13057" data-mp="zmb2oiq58dig">{yAxisLabel || 'Waarde'}</th>
          </tr>
        </thead>
        <tbody data-id="element-13058" data-mp="14fm9wkyvawaw">
          {data.map((item, i) => <tr key={i} data-id="element-13059" data-mp="14gnlcqjspkt5" data-mpi={i}>
              <td data-id="element-13060" data-mp="1eab2gaemtgb7">{item.label}</td>
              <td data-id="element-13061" data-mp="1e99s685fwxuj">{item.value}</td>
            </tr>)}
        </tbody>
      </table>

      <div className="w-full h-[240px] md:h-[340px] bg-white rounded-xl border border-slate-200 shadow-sm p-3 md:p-4" data-id="element-13062" data-mp="19b88pm43qwij">
        <ResponsiveContainer width="100%" height="100%" data-id="element-13063" data-mp="158jypb3njjjz">
          <BarChart data={data} margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 40
        }} data-id="element-13064" data-mp="10b1z4bbiygon">
            
            <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-13065" data-mp="vd9xgpl2vg79" />
            <XAxis dataKey="label" interval={0} tick={{
            fill: '#64748b',
            fontSize: 12
          }} tickLine={false} axisLine={{
            stroke: '#cbd5e1'
          }} label={xAxisLabel ? {
            value: xAxisLabel,
            position: 'insideBottom',
            offset: -20,
            fill: '#475569',
            fontSize: 13,
            fontWeight: 600
          } : undefined} data-id="element-13066" data-mp="qkqj3qekgh1e" />
            

            <YAxis tick={{
            fill: '#64748b',
            fontSize: 12
          }} tickLine={false} axisLine={false} label={yAxisLabel ? {
            value: yAxisLabel,
            angle: -90,
            position: 'insideLeft',
            fill: '#475569',
            fontSize: 14,
            fontWeight: 600
          } : undefined} data-id="element-13067" data-mp="7mmmfvfv8agh" />
            

            <Tooltip content={<CustomTooltip data-id="element-13069" data-mp="2x7xxr2ybzkdn" />} cursor={{
            fill: '#f1f5f9'
          }} data-id="element-13068" data-mp="2x9irwrgqh1az" />
            

            {effectiveValueDisplay === 'on-axis-line' && Array.from(new Set(data.map(d => d.value))).map((val, i) => <ReferenceLine key={`ref-${i}`} y={val} stroke="#94a3b8" strokeDasharray="3 3" opacity={0.5} data-id="element-13070" data-mp="25iuunqi6kg2l" data-mpi={i} />)}

            <Bar dataKey="value" radius={[4, 4, 0, 0]} data-id="element-13071" data-mp="29i3o0u9b6bxm">
              {effectiveValueDisplay === 'on-bar' && <LabelList dataKey="value" position="top" fill="#475569" fontSize={12} fontWeight={600} data-id="element-13072" data-mp="2eby2nja240pu" />}
              {data.map((_entry, index) => <Cell key={`cell-${index}`} fill={barColor} data-id="element-13073" data-mp="1hqnyosgdab3p" data-mpi={index} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>;
}